import { useEffect, useRef, useState } from 'react';
import { clientApi } from '@/config/axios';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

type Source = {
  document_id: string;
  revision_id: string;
  source_hash: string;
  policy_version: number;
  title: string;
  revision_number: number;
  can_start: boolean;
  job: { id: string; status: string; created_at: string } | null;
};
type Sources = {
  items: Source[];
  has_more: boolean;
  processing_enabled: boolean;
  can_trigger: boolean;
  checked_at: string;
};
const statusLabels: Record<string, string> = {
  queued: 'Waiting to process',
  running: 'Processing',
  ready: 'Ready for intake review',
  needs_human: 'Review details',
  not_referral: 'Records only',
  failed: 'Processing failed',
};

export function ReferralWork({ base, onReview }: { base: string; onReview: (jobId: string) => void }) {
  const [sources, setSources] = useState<Sources | null>(null);
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [selection, setSelection] = useState<Source | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const mutation = useRef<AbortController | null>(null);
  useEffect(() => () => mutation.current?.abort(), []);

  useEffect(() => {
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function load() {
      try {
        const { data } = await clientApi.get<Sources>(`${base}/referrals?offset=${offset}`, { signal: abort.signal });
        if (abort.signal.aborted) return;
        setSources(data);
        setError('');
        // Poll only live jobs, only while this view is mounted. Never retry a start automatically.
        if (data.items.some((item) => item.job && ['queued', 'running'].includes(item.job.status))) {
          timer = setTimeout(() => void load(), 5000);
        }
      } catch {
        if (!abort.signal.aborted) {
          setSources(null);
          setSelection(null);
          setError('Referral work is unavailable. Access may have changed; refresh or use the referral inbox.');
        }
      }
    }
    void load();
    return () => {
      abort.abort();
      clearTimeout(timer);
    };
  }, [base, offset, refresh]);

  function reload() {
    setSelection(null);
    setSources(null);
    setError('');
    setRefresh((value) => value + 1);
  }
  async function start() {
    if (!selection || mutation.current) return;
    const abort = new AbortController();
    mutation.current = abort;
    setBusy(true);
    setError('');
    try {
      await clientApi.post(
        `${base}/referrals/start`,
        {
          request_id: crypto.randomUUID(),
          document_id: selection.document_id,
          revision_id: selection.revision_id,
          source_hash: selection.source_hash,
          policy_version: selection.policy_version,
          confirmed: true,
        },
        { signal: abort.signal },
      );
      if (!abort.signal.aborted) {
        setNotice('Processing requested. Existing work is reused when this source has already been queued.');
        reload();
      }
    } catch (cause) {
      if (!abort.signal.aborted) {
        const status = (cause as { response?: { status?: number } }).response?.status;
        setSelection(null);
        setSources(null);
        setError(
          status === 409
            ? 'The source or processing settings changed, or a processing limit was reached. Refresh before trying again.'
            : status === 403 || status === 404
              ? 'This source or referral workflow is no longer accessible.'
              : 'Could not confirm processing. Refresh to check its status before trying again.',
        );
      }
    } finally {
      if (!abort.signal.aborted) {
        mutation.current = null;
        setBusy(false);
      }
    }
  }
  return (
    <section className="flex flex-col gap-4" aria-label="Patient referral work">
      <Alert>
        <AlertTitle>Prepare for review</AlertTitle>
        <AlertDescription>
          Select a saved source linked to this patient. Processing prepares extracted fields and a pre-visit summary.
          Review and save intake here before extracted information is applied to the chart.
        </AlertDescription>
      </Alert>
      {notice && <p role="status">{notice}</p>}
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Unable to load referral work</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!sources && !error && <p role="status">Loading linked sources…</p>}
      <Button variant="outline" disabled={busy} onClick={reload}>
        Refresh sources
      </Button>
      {sources && (
        <>
          <p className="text-sm text-muted-foreground">Checked {new Date(sources.checked_at).toLocaleString()}</p>
          {!sources.processing_enabled && (
            <p>New referral processing is disabled. Existing work can still be reviewed.</p>
          )}
          {!sources.can_trigger && <p>Your access allows review but not starting processing.</p>}
          {!sources.items.length && (
            <p>
              No eligible saved text on this page. Incoming faxes awaiting patient identification are available in
              Inbox → Incoming Fax Assistant.
            </p>
          )}
          {sources.items.map((item) => (
            <Card key={item.document_id}>
              <CardHeader>
                <CardTitle>{item.title}</CardTitle>
                <CardDescription>Saved source · Revision {item.revision_number}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <Badge variant="secondary">
                  {item.job
                    ? statusLabels[item.job.status] || 'Review processing status'
                    : 'Not yet processed with current settings'}
                </Badge>
                {item.job && (
                  <p className="text-sm">
                    Requested {new Date(item.job.created_at).toLocaleString()}. Intake requires human review.
                  </p>
                )}
              </CardContent>
              <CardFooter className="flex flex-col items-start gap-3">
                {item.job && (
                  <Button variant="outline" disabled={busy} onClick={() => onReview(item.job!.id)}>
                    Open referral review
                  </Button>
                )}
                {item.can_start && selection?.document_id !== item.document_id && (
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setSelection(item);
                      setNotice('');
                    }}>
                    Select source for processing
                  </Button>
                )}
                {selection?.document_id === item.document_id && (
                  <div className="flex flex-col gap-3">
                    <p>
                      Process “{selection.title}”, revision {selection.revision_number}, for review? This uses your
                      practice’s existing processing allowance.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button disabled={busy} onClick={() => void start()}>
                        {busy ? 'Requesting processing…' : 'Start processing for review'}
                      </Button>
                      <Button variant="ghost" disabled={busy} onClick={() => setSelection(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardFooter>
            </Card>
          ))}
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={busy || offset === 0}
              onClick={() => {
                setSelection(null);
                setSources(null);
                setOffset(Math.max(0, offset - 20));
              }}>
              Newer sources
            </Button>
            <Button
              variant="outline"
              disabled={busy || !sources.has_more}
              onClick={() => {
                setSelection(null);
                setSources(null);
                setOffset(offset + 20);
              }}>
              Older sources
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
